// feature/directora-landing — Doctora Tania (Directora) landing page.
// Three sections: greeting + action-required cards + 4-card stats grid + activity feed.
// Stagger fade-in via CSS animation-delay; numbers count up on mount.
import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../api/client";
import { admin as adminApi, cases as casesApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import owl from "../../assets/athena_owl.svg";
import { useCaseLauncher } from "../../components/CaseLauncher";
import { useRefreshOnNotification } from "../../components/useRefreshOnNotification";
import { useHoverIntent } from "../../hooks/useHoverIntent";
import { HoverFocusProvider, useHoverFocus } from "../../context/HoverFocusContext";
import { rolesOf } from "../../utils/permissions";

const DIAS_ES  = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
const MESES_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function greetingForHour(h) {
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

function fmtFullDate(d = new Date()) {
  return `${DIAS_ES[d.getDay()]}, ${d.getDate()} de ${MESES_ES[d.getMonth()]} de ${d.getFullYear()}`;
}

function timeAgo(d) {
  if (!d) return "";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

// Smooth count-up animation, 450ms cubic-out.
function useCountUp(target, durationMs = 450) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target) || target === 0) { setN(target || 0); return; }
    const start = performance.now();
    let raf;
    function tick(now) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

export default function DirectoraDashboard() {
  return (
    <HoverFocusProvider>
      <DirectoraDashboardInner />
    </HoverFocusProvider>
  );
}

function DirectoraDashboardInner() {
  const { user } = useAuth();
  const launcher = useCaseLauncher();
  const [now, setNow] = useState(() => new Date());
  const [stats, setStats] = useState(null);
  const [devueltos, setDevueltos] = useState([]);
  const [auditLog, setAuditLog] = useState(null);
  // feature/notifications-clear — pagination on the activity log.
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(true);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const AUDIT_PAGE = 20;

  function load() {
    Promise.all([
      casesApi.list(),
      api.get("/api/admin/devueltos"),
      adminApi.lateEntryQueue(),
      adminApi.auditLog({ limit: AUDIT_PAGE }),
    ]).then(([casesRes, devRes, lateRes, auditRes]) => {
      const all = casesRes.data || [];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const isToday = (d) => d && new Date(d) >= today;

      const ACTIVE = ["pendiente_insumos","insumos_solicitados","expediente_solicitado","insumos_recibidos","en_elaboracion","en_auditoria","devuelto_a_reparto"];
      const breached = all.filter(c => {
        if (!c.termino_horas || !c.fecha_ingreso) return false;
        if (["cerrado","aprobado","en_litisoft","fallo_decidido_no_impugnar","error_marcado"].includes(c.estado)) return false;
        if (c.sla_paused_at) return false;
        const elapsedH = (Date.now() - new Date(c.fecha_ingreso).getTime()) / 3600000;
        return elapsedH > Number(c.termino_horas);
      }).length;

      setStats({
        activos: all.filter(c => ACTIVE.includes(c.estado)).length,
        cerrados_hoy: all.filter(c => ["cerrado","en_litisoft"].includes(c.estado) && isToday(c.updated_at)).length,
        devueltos_pendientes: (devRes.data || []).length,
        late_entries_hoy: (lateRes.data || []).filter(c => isToday(c.created_at)).length,
        breaches: breached,
      });
      setDevueltos(devRes.data || []);
      const audit = auditRes.data || [];
      setAuditLog(audit);
      setAuditOffset(audit.length);
      setAuditHasMore(audit.length === AUDIT_PAGE);
    }).catch(() => {
      setStats({ activos: 0, cerrados_hoy: 0, devueltos_pendientes: 0, late_entries_hoy: 0, breaches: 0 });
      setAuditLog([]);
      setAuditHasMore(false);
    });
  }

  async function loadMoreAudit() {
    if (auditLoadingMore || !auditHasMore) return;
    setAuditLoadingMore(true);
    try {
      const r = await adminApi.auditLog({ limit: AUDIT_PAGE, offset: auditOffset });
      const next = r.data || [];
      setAuditLog(prev => [...(prev || []), ...next]);
      setAuditOffset(o => o + next.length);
      setAuditHasMore(next.length === AUDIT_PAGE);
    } catch { /* silent */ }
    finally { setAuditLoadingMore(false); }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => { setNow(new Date()); load(); }, 60_000);
    return () => clearInterval(t);
  }, []);
  // feature/smart-refresh — refresh dashboard on any notification arrival.
  useRefreshOnNotification(null, () => { setNow(new Date()); load(); });

  const firstName = (user?.name || "").split(/\s+/)[0] || "Doctora";
  const greeting = greetingForHour(now.getHours());
  const dateLabel = fmtFullDate(now);

  return (
    <div className="dir-page">
      <style>{`
        @keyframes dir-fade-up {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .dir-page { display: flex; flex-direction: column; gap: 32px; padding: 8px 0 32px; }
        .dir-fade { opacity: 0; animation: dir-fade-up .35s ease-out forwards; }

        /* feature/glass-sections — stat/action cards lift via depth tokens + spring physics. */
        .dir-stat-card {
          background: var(--bg-card);
          border: 1px solid var(--border-hairline);
          border-radius: 14px;
          padding: 24px;
          transition: box-shadow var(--motion-base) var(--motion-spring-soft),
                      transform  var(--motion-base) var(--motion-spring-soft);
        }
        .dir-stat-card:hover { box-shadow: var(--shadow-level-1); transform: translateY(-2px); }

        .dir-action-card {
          background: var(--bg-card);
          border: 1px solid var(--border-hairline);
          border-radius: 14px;
          padding: 20px 24px;
          display: flex; align-items: center; gap: 24px;
          transition: box-shadow var(--motion-base) var(--motion-spring-soft),
                      transform  var(--motion-base) var(--motion-spring-soft);
        }
        .dir-action-card:hover { box-shadow: var(--shadow-level-2); transform: translateY(-2px); }
        .dir-action-card.urgent { background: var(--color-warning-bg); border-color: var(--color-warning); }
        .dir-action-card.bad    { background: var(--color-danger-bg);  border-color: var(--color-danger); }
        .dir-action-card.calm   { background: var(--accent-mint-bg);   border-color: var(--accent-mint); }

        /* Audit feed rows now individual rounded items (no shared bottom-border). */
        .dir-feed-row {
          display: flex; gap: 12px;
          padding: 12px 14px;
          border-radius: 12px;
          cursor: pointer;
        }
        .dir-feed-row + .dir-feed-row { margin-top: 4px; }

        .dir-avatar { width: 32px; height: 32px; border-radius: 50%; background: var(--accent-mint-bg);
                      color: var(--accent-mint); display: inline-flex; align-items: center; justify-content: center;
                      font-size: 11px; font-weight: 600; flex-shrink: 0; }

        @media (prefers-reduced-motion: reduce) {
          .dir-stat-card:hover, .dir-action-card:hover { transform: none; }
        }
      `}</style>

      <header className="dir-fade" style={{ animationDelay: "0ms" }}>
        <div style={{ fontSize: 28, fontWeight: 600, color: "var(--text-primary)" }}>
          {/* feature/single-role-audit — multi-role aware so Tania
              (admin/carpetas/directora, primary admin) still gets
              the "Doctora" prefix here. */}
          {greeting}, {rolesOf(user).includes("directora") ? "Doctora " : ""}{firstName}
        </div>
        <div className="dir-fade" style={{ animationDelay: "100ms", fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>
          {dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
        </div>
      </header>

      {stats == null ? (
        <div style={{ padding: 48, textAlign: "center" }}><Spinner size={20} /></div>
      ) : (
        <>
          <ActionRequiredSection stats={stats} />
          <StatsGrid stats={stats} />
          <ActivitySection
            auditLog={auditLog}
            onOpenCase={(id) => launcher.open(id)}
            hasMore={auditHasMore}
            loadingMore={auditLoadingMore}
            onLoadMore={loadMoreAudit}
          />
        </>
      )}
    </div>
  );
}

function ActionRequiredSection({ stats }) {
  const allClear = !stats.devueltos_pendientes && !stats.late_entries_hoy && !stats.breaches;
  if (allClear) {
    return (
      <section className="dir-fade" style={{ animationDelay: "150ms" }}>
        <div className="dir-action-card calm" style={{ justifyContent: "center", textAlign: "center", flexDirection: "column", padding: 32 }}>
          <img src={owl} alt="" style={{ width: 48, height: 48, opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--accent-mint)" }}>Todo en orden</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>No hay nada urgente.</div>
        </div>
      </section>
    );
  }
  return (
    <section className="dir-fade" style={{ animationDelay: "150ms", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
      {stats.devueltos_pendientes > 0 && (
        <ActionCard tone="urgent" count={stats.devueltos_pendientes} label="Devueltos por insumo inválido" cta="Ver" href="/directora/devueltos" />
      )}
      {stats.late_entries_hoy > 0 && (
        <ActionCard tone="urgent" count={stats.late_entries_hoy} label="Registros extemporáneos hoy" cta="Ver" href="/admin/late-entry-queue" />
      )}
      {stats.breaches > 0 && (
        <ActionCard tone="bad" count={stats.breaches} label="SLA vencidos" cta="Ver" href="/admin/sla-breaches" />
      )}
    </section>
  );
}

function ActionCard({ tone, count, label, cta, href }) {
  const n = useCountUp(count);
  return (
    <a href={href} className={"dir-action-card action-required-card " + (tone || "")}
       style={{ textDecoration: "none", color: "inherit" }}>
      <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1, color: tone === "bad" ? "var(--color-danger)" : "var(--color-warning)" }}>
        {n}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: "var(--text-body)" }}>{label}</div>
      </div>
      <div style={{ fontSize: 13, color: "var(--accent-mint)", fontWeight: 500 }}>{cta} →</div>
    </a>
  );
}

function StatsGrid({ stats }) {
  return (
    <section className="dir-fade" style={{ animationDelay: "200ms" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Stat label="Casos activos hoy" value={stats.activos} />
        <Stat label="Cerrados hoy"      value={stats.cerrados_hoy} />
        <Stat label="Devueltos pendientes" value={stats.devueltos_pendientes} tone={stats.devueltos_pendientes ? "warning" : null} />
        <Stat label="Registros extemporáneos hoy" value={stats.late_entries_hoy} tone={stats.late_entries_hoy ? "info" : null} />
      </div>
    </section>
  );
}

function Stat({ label, value, tone }) {
  const n = useCountUp(value);
  const color = tone === "warning" ? "var(--color-warning)"
              : tone === "info"    ? "var(--color-info)"
              : "var(--text-primary)";
  return (
    <div className="dir-stat-card stat-card">
      <div className="label-uc">{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color, marginTop: 8 }}>{n}</div>
    </div>
  );
}

function ActivitySection({ auditLog, onOpenCase, hasMore, loadingMore, onLoadMore }) {
  const items = auditLog || [];
  const { focusedRowId } = useHoverFocus();
  return (
    <section className="dir-fade" style={{ animationDelay: "300ms" }}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Actividad reciente</h2>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
          Toda la firma · últimas 24 horas
        </div>
      </div>
      <div className="card dashboard-card" style={{ padding: "12px" }}>
        {auditLog == null ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
        ) : !items.length ? (
          <div className="muted" style={{ padding: 24, textAlign: "center", fontSize: 13 }}>
            Sin actividad reciente.
          </div>
        ) : (
          <div className={"eds-focus-list" + (focusedRowId ? " has-focused-row" : "")}>
            {items.map((e, idx) => (
              <FeedRow key={e.id} e={e} idx={idx} onOpenCase={onOpenCase} />
            ))}
            {hasMore && (
              <button
                className="activity-load-more"
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Cargando…" : "Cargar más actividad"}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function FeedRow({ e, idx, onOpenCase }) {
  const { setFocusedRowId } = useHoverFocus();
  const intent = useHoverIntent(300, 100);
  useEffect(() => {
    if (intent.isFocused) setFocusedRowId(e.id);
    else setFocusedRowId(prev => (prev === e.id ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.isFocused, e.id]);
  return (
    <div
      className={"dir-fade dir-feed-row eds-focus-row" + (intent.isFocused ? " is-focused" : "")}
      style={{ animationDelay: `${300 + idx * 30}ms` }}
      onClick={() => e.case_id && onOpenCase(e.case_id)}
      onMouseEnter={intent.onMouseEnter}
      onMouseLeave={intent.onMouseLeave}
    >
      <div className="dir-avatar">
        {(e.actor_name || "S").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase()}
      </div>
      <div style={{ flex: 1, fontSize: 13 }}>
        <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{e.actor_name || "Sistema"}</span>{" "}
        <span style={{ color: "var(--text-muted)" }}>{describeAction(e)}</span>
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{timeAgo(e.created_at)}</div>
    </div>
  );
}

function describeAction(e) {
  const cn = e.case_number ? ` ${e.case_number}` : "";
  switch (e.action) {
    case "case.create":              return `creó${cn}`;
    case "case.update":              return `actualizó${cn}`;
    case "case.reassign":            return `reasignó${cn}`;
    case "send_audit":               return `envió${cn} a auditoría`;
    case "approve":                  return `aprobó${cn}`;
    case "reject":                   return `devolvió${cn} con observaciones`;
    case "decide_impugnar":          return `decidió impugnación de${cn}`;
    case "case.devuelto_invalido_insumo": return `marcó${cn} como insumos inválidos`;
    case "case.devuelto_resuelto":   return `marcó${cn} como resuelto`;
    case "insumo.response_uploaded": return `subió respuesta de insumo en${cn}`;
    case "case.document_uploaded":   return `subió documento en${cn}`;
    case "document.replaced":        return `reemplazó documento en${cn}`;
    case "document.soft_deleted":    return `eliminó documento en${cn}`;
    case "document.restored":        return `restauró documento en${cn}`;
    case "case.flag_error":          return `marcó${cn} como error`;
    case "case.hard_deleted_by_admin": return `eliminó permanentemente${cn || " un caso"}`;
    case "login":                    return "inició sesión";
    case "logout":                   return "cerró sesión";
    default:                         return `${e.action}${cn}`;
  }
}
